// public/js/features/devices/devices-page.js
import { loadDevices, grantDevices } from '../../features/devices/api.js';
import { loadTenants } from '../../modules/tenants.js';
import { showActionMenu } from './components/device-action-menu.js';
import { renderFilterPanelHTML, attachFilterListeners, applyFilters } from './devices-filter.js';
import { createTableSorter } from '../../utils/table-sort.js';
import { openDeviceDetailModal } from './components/device-detail-modal.js';
import { deviceTypeLabel } from './device-type.js';
import { manualHrefForDevice } from './branded-models.js';
import { batteryRemainingPill, batterySortValue, wireBatteryHelp } from '../tracker-config/battery-remaining-ui.js';
import { can, getCurrentUser } from '../../modules/rbac.js';
import { tName } from '../../modules/demo-mode.js';
import { escapeHtml } from '../../utils/escape.js';
import { scopeTenantParam, isAllTenants, getMemberships } from '../../modules/tenant-scope.js';

let allDevices = [];
// Platform roles get a bulk-grant column + toolbar; everyone else sees the
// plain table. Computed once per page load.
const canGrant = can(getCurrentUser().groups || [], 'grantDevice');
const selected = new Set();   // deviceIds ticked for a bulk grant

export async function initDevicesPage(container) {
  // Under a single tenant scope the tenant column is redundant (every row is
  // the same) — it only shows under "All tenants". The page re-inits on scope
  // changes, so this is fixed per render.
  const showTenantCol = isAllTenants();

  // Column order must match the <thead> below. A leading checkbox column exists
  // only for platform roles. Non-sortable: the checkbox and the actions cells.
  const DEVICE_SORT_COLUMNS = [
    ...(canGrant ? [{ sortable: false }] : []),
    { type: 'string', sortable: true, accessor: d => d.deviceId || '' },
    { type: 'string', sortable: true, accessor: d => deviceTypeLabel(d) },
    { type: 'string', sortable: true, accessor: d => d.serialNumber || d.IMEI || d.SerNo || '' },
    ...(showTenantCol ? [{ type: 'string', sortable: true, accessor: d => d.tenantName || '' }] : []),
    { type: 'number', sortable: true, accessor: d => (d.assetUUID ? 1 : 0) },
    { type: 'date',   sortable: true, accessor: d => d.lastSeen || '' },
    // Battery: sort by estimated remaining DAYS (soonest first), not by the
    // voltage that used to sit here - the pill is the only thing the column shows.
    { type: 'number', sortable: true, accessor: d => batterySortValue(d) },
    { sortable: false }, // actions
  ];
  const COLSPAN = DEVICE_SORT_COLUMNS.length;
  container.innerHTML = `
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="h4 mb-0">Devices / Tracker <span class="badge bg-primary ms-2" id="devices-count-badge">0</span></h2>
      <button id="btn-refresh-devices" class="btn btn-outline-primary btn-sm">
        <i class="fa-solid fa-arrows-rotate"></i> Refresh
      </button>
    </div>

    <div id="devices-filter-slot"></div>

    ${canGrant ? `
    <div id="device-bulk-bar" class="d-none align-items-center gap-2 mb-2 p-2 rounded border bg-body-tertiary">
      <span class="fw-semibold"><span id="bulk-count">0</span> selected</span>
      <span class="text-muted small">→ grant to</span>
      <select id="bulk-tenant" class="form-select form-select-sm" style="max-width:260px"></select>
      <button id="bulk-grant-btn" class="btn btn-primary btn-sm" disabled>
        <i class="fa-solid fa-building-circle-check me-1"></i> Grant
      </button>
      <button id="bulk-clear-btn" class="btn btn-link btn-sm text-muted">Clear</button>
    </div>` : ''}

    <div class="card shadow-sm">
      <div class="card-body p-0 table-responsive">
        <table class="table table-hover mb-0" id="devices-table">
          <thead class="table-light">
            <tr>
              ${canGrant ? `<th style="width:36px;"><input type="checkbox" id="bulk-select-all" class="form-check-input" title="Select all shown"></th>` : ''}
              <th>Device ID</th>
              <th>Type</th>
              <th>Serial / ESN</th>
              ${showTenantCol ? '<th>Tenant</th>' : ''}
              <th>Assigned Asset</th>
              <th>Last Seen</th>
              <th>Battery</th>
              <th style="width: 50px;"></th>
            </tr>
          </thead>
          <tbody></tbody>
        </table>
      </div>
    </div>
  `;

  const tbody = container.querySelector('tbody');
  const countBadge = document.getElementById('devices-count-badge');

  // Click-to-sort on the (static) table header.
  const sorter = createTableSorter(DEVICE_SORT_COLUMNS, () => renderTable());
  sorter.decorate(container.querySelector('#devices-table thead tr'));

  function renderTable() {
    const filtered = sorter.sort(applyFilters(allDevices));
    const total = allDevices.length;
    countBadge.textContent = filtered.length < total ? `${filtered.length} / ${total}` : total;

    if (filtered.length === 0) {
      tbody.innerHTML = `<tr><td colspan="${COLSPAN}" class="text-center text-muted py-4">
        ${total === 0 ? 'No devices found.' : 'No devices match the current filters.'}
      </td></tr>`;
      if (canGrant) syncBulkBar(filtered);
      return;
    }

    const L = navigator.language;
    tbody.innerHTML = filtered.map(device => `
      <tr data-device-id="${escapeHtml(device.deviceId)}" class="device-row" style="cursor:pointer;">
        ${canGrant ? `<td class="text-center"><input type="checkbox" class="form-check-input row-select" data-device-id="${escapeHtml(device.deviceId)}" ${selected.has(device.deviceId) ? 'checked' : ''}></td>` : ''}
        <td><strong>${escapeHtml(device.deviceId)}</strong></td>
        <td>${(() => {
          const href = manualHrefForDevice(device), label = deviceTypeLabel(device);
          return href
            ? `<a href="${href}" class="badge bg-secondary text-decoration-none manual-link" title="Open in the device manual">${label} <i class="fa-solid fa-book ms-1"></i></a>`
            : `<span class="badge bg-secondary">${label}</span>`;
        })()}</td>
        <td>${escapeHtml(device.serialNumber || device.IMEI || device.SerNo || '—')}</td>
        ${showTenantCol ? `<td>${device.tenantId
          ? `<span class="badge bg-info">${tName(device.tenantName)}</span>`
          : `<span class="badge badge-soft-amber">ownerless</span>`}</td>` : ''}
        <td class="text-center" style="width: 60px;">
          ${device.assetUUID
            ? `<a href="#assets/${encodeURIComponent(device.assetUUID)}" class="text-success text-decoration-none asset-link" title="Open the paired asset">
                 <i class="fa-solid fa-check-circle fa-lg"></i></a>`
            : `<i class="fa-solid fa-times-circle text-danger fa-lg" title="Unassigned"></i>`
          }
        </td>
        <td><small class="text-muted">${device.lastSeen ? new Date(device.lastSeen).toLocaleString(L) : '—'}</small></td>
        <td>
          ${batteryRemainingPill(device) || '<span class="text-muted">—</span>'}
        </td>
        <td>
          <button class="btn btn-sm btn-link text-dark action-menu" data-device-id="${device.deviceId}">⋮</button>
        </td>
      </tr>
    `).join('');
    wireBatteryHelp(tbody, filtered);

    tbody.querySelectorAll('.action-menu').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        const device = allDevices.find(d => d.deviceId === btn.dataset.deviceId);
        if (device) showActionMenu(e, device, loadDevicesTable);
      });
    });

    // Checkbox clicks toggle selection without opening the detail modal.
    if (canGrant) {
      tbody.querySelectorAll('.row-select').forEach(cb => {
        cb.addEventListener('click', e => e.stopPropagation());
        cb.addEventListener('change', () => {
          if (cb.checked) selected.add(cb.dataset.deviceId);
          else selected.delete(cb.dataset.deviceId);
          syncBulkBar(filtered);
        });
      });
      syncBulkBar(filtered);
    }

    // Row click → device detail modal (ignore clicks on menu/checkbox).
    tbody.querySelectorAll('.device-row').forEach(row => {
      row.addEventListener('click', e => {
        // The paired-asset link navigates on its own — don't also open the modal.
        if (e.target.closest('.action-menu') || e.target.closest('.row-select') || e.target.closest('.asset-link') || e.target.closest('.manual-link')) return;
        const device = allDevices.find(d => d.deviceId === row.dataset.deviceId);
        if (device) openDeviceDetailModal(device);
      });
    });
  }

  // Reflect the current selection in the bulk toolbar + the header checkbox.
  function syncBulkBar(shownDevices) {
    if (!canGrant) return;
    const bar = document.getElementById('device-bulk-bar');
    document.getElementById('bulk-count').textContent = selected.size;
    document.getElementById('bulk-grant-btn').disabled = selected.size === 0;
    bar.classList.toggle('d-none', selected.size === 0);

    const all = document.getElementById('bulk-select-all');
    if (all) {
      const shownIds = shownDevices.map(d => d.deviceId);
      const shownSelected = shownIds.filter(id => selected.has(id)).length;
      all.checked = shownIds.length > 0 && shownSelected === shownIds.length;
      all.indeterminate = shownSelected > 0 && shownSelected < shownIds.length;
    }
  }

  async function loadDevicesTable() {
    window.showPlaneSpinner(null, 'Loading devices...');
    try {
      // Scoped server load (?tenant=). Tenant names fall back to the scope
      // memberships when the caller lacks the listTenants permission.
      const [rawDevices, tenants] = await Promise.all([
        loadDevices(scopeTenantParam()),
        loadTenants().catch(() => getMemberships()),
      ]);
      const tenantMap = new Map(tenants.map(t => [t.tenantId, t.name || t.tenantId]));

      allDevices = rawDevices.map(d => ({
        ...d,
        tenantName: tenantMap.get(d.tenantId) || d.tenantId || '—',
      }));

      // Build the filter panel (distinct fine-grained type labels + tenants).
      const types = [...new Set(allDevices.map(d => deviceTypeLabel(d)).filter(Boolean))]
        .sort()
        .map(t => ({ value: t, text: t }));
      document.getElementById('devices-filter-slot').innerHTML = renderFilterPanelHTML({ tenants, types });
      attachFilterListeners(renderTable);

      // Fill the grant target dropdown (keep any current pick).
      if (canGrant) {
        const sel = document.getElementById('bulk-tenant');
        const keep = sel.value;
        sel.classList.add('tenant-blur'); // blur the selected tenant in demo mode
        sel.innerHTML = `<option value="" disabled ${keep ? '' : 'selected'}>Select tenant…</option>`
          + tenants.map(t => `<option value="${escapeHtml(t.tenantId)}" ${t.tenantId === keep ? 'selected' : ''}>${escapeHtml(t.name || t.tenantId)}</option>`).join('');
      }

      renderTable();
    } catch (err) {
      console.error(err);
      tbody.innerHTML = `<tr><td colspan="${COLSPAN}" class="text-center text-danger py-4">Error loading devices</td></tr>`;
      window.showToast('Failed to load devices', 'danger');
    } finally {
      window.hidePlaneSpinner();
    }
  }

  document.getElementById('btn-refresh-devices').addEventListener('click', loadDevicesTable);
  window.refreshDevices = loadDevicesTable;

  if (canGrant) wireBulkGrant();

  await loadDevicesTable();

  // ── Bulk-grant toolbar (platform roles) ───────────────────────────────────
  function wireBulkGrant() {
    const selectAll = document.getElementById('bulk-select-all');
    const grantBtn  = document.getElementById('bulk-grant-btn');
    const clearBtn  = document.getElementById('bulk-clear-btn');

    selectAll.addEventListener('change', () => {
      const shown = applyFilters(allDevices);      // respect active filters
      shown.forEach(d => selectAll.checked ? selected.add(d.deviceId) : selected.delete(d.deviceId));
      renderTable();
    });

    clearBtn.addEventListener('click', () => { selected.clear(); renderTable(); });

    grantBtn.addEventListener('click', async () => {
      const tenantSel = document.getElementById('bulk-tenant');
      const tenantId = tenantSel.value;
      const ids = [...selected];
      if (!tenantId || !ids.length) return;
      const tenantName = tenantSel.options[tenantSel.selectedIndex]?.text || tenantId;
      if (!confirm(`Grant ${ids.length} device${ids.length === 1 ? '' : 's'} to “${tenantName}”?`)) return;

      grantBtn.disabled = true;
      try {
        const r = await grantDevices(ids, tenantId);
        window.showToast(
          `Granted ${r.grantedCount} device${r.grantedCount === 1 ? '' : 's'} to ${tenantName}`
          + (r.failed?.length ? ` — ${r.failed.length} failed` : ''),
          r.failed?.length ? 'warning' : 'success');
        selected.clear();
        await loadDevicesTable();
      } catch (err) {
        window.showToast(err.message || 'Grant failed', 'danger');
        grantBtn.disabled = false;
      }
    });
  }
}
